import React, { useState } from 'react';
import { Button, Spinner, Badge } from 'react-bootstrap';
import { FaEnvelope } from 'react-icons/fa';
import MatchDetailsPopup from './MatchDetailsPopup';
import '../../styles/EnhancedLenderButton.css';

/**
 * EnhancedLenderButton Component
 * 
 * Enhanced submit button that integrates lender match information
 * Shows progress bar background, match ratio, and detailed popup on hover
 * 
 * @param {Object} props - Component props
 * @param {Object} props.lender - Lender object with id, name, etc.
 * @param {Object} props.matchData - Match data object with score, criteria, etc.
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {Function} props.onSubmit - Function to call when button is clicked
 * @param {string} props.submissionType - Type of submission ('api' or 'email')
 * @param {string} props.variant - Button variant ('outline-primary' or 'outline-secondary')
 * @returns {JSX.Element} EnhancedLenderButton component
 */
const EnhancedLenderButton = ({
  lender,
  matchData = null,
  isSubmitting = false,
  disabled = false,
  onSubmit,
  submissionType = 'api',
  variant = 'outline-primary'
}) => {
  const [showMatchPopup, setShowMatchPopup] = useState(false);

  // Extract match information
  const {
    score = 0,
    matched = [],
    missed = [],
    incomplete = [],
    disqualified = false
  } = matchData || {};

  // Calculate totals
  const totalCriteria = matched.length + missed.length + incomplete.length;
  const hasCriteria = totalCriteria > 0;
  const isAutoDecline = disqualified || (missed.some(c => c.required) && missed.filter(c => c.required).every(c => c.actualValue !== undefined && c.actualValue !== null && c.actualValue !== ''));

  // Get score variant for styling
  const getScoreVariant = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'info';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const scoreVariant = getScoreVariant(score);

  // Handle button click
  const handleClick = () => {
    if (!disabled && !isSubmitting) {
      onSubmit();
    }
  };

  // Handle details button click
  const handleDetailsClick = (e) => {
    e.stopPropagation(); // Prevent triggering the submit button
    setShowMatchPopup(true);
  };

  // Determine button styling based on match data
  const getButtonStyling = () => {
    if (isAutoDecline && hasCriteria) {
      return {
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        borderColor: '#dc3545',
        color: '#dc3545'
      };
    }

    if (incomplete.length > 0 && hasCriteria) {
      return {
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        borderColor: '#ffc107',
        color: '#856404'
      };
    }

    if (hasCriteria && score > 0) {
      const alpha = Math.max(0.05, Math.min(0.2, score / 500)); // Scale opacity based on score
      const colors = {
        success: 'rgba(25, 135, 84, ',
        info: 'rgba(13, 202, 240, ',
        warning: 'rgba(255, 193, 7, ',
        danger: 'rgba(220, 53, 69, '
      };
      
      return {
        backgroundColor: `${colors[scoreVariant]}${alpha})`,
        borderColor: variant === 'outline-primary' ? '#0d6efd' : '#6c757d'
      };
    }

    return {};
  };

  // Create progress bar overlay
  const progressBarOverlay = hasCriteria && score > 0 ? (
    <div 
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: '4px',
        width: `${score}%`,
        backgroundColor: isAutoDecline ? '#dc3545' : (incomplete.length > 0 ? '#ffc107' : 
          scoreVariant === 'success' ? '#198754' :
          scoreVariant === 'info' ? '#0dcaf0' :
          scoreVariant === 'warning' ? '#ffc107' : '#dc3545'),
        borderRadius: '0 0 0 0.375rem',
        transition: 'width 0.3s ease'
      }}
    />
  ) : null;

  return (
    <>
      <div className="position-relative">
        <Button
          variant={variant}
          className={`w-100 position-relative enhanced-lender-button ${
            isAutoDecline ? 'auto-decline' : 
            incomplete.length > 0 ? 'pending-input' : ''
          }`}
          style={{
            minHeight: '60px',
            overflow: 'hidden',
            ...getButtonStyling()
          }}
          disabled={disabled || isSubmitting}
          onClick={handleClick}
        >
          {/* Progress bar overlay */}
          {progressBarOverlay}
          
          {/* Button content */}
          <div className="enhanced-button-content">
            {/* Top row: Lender name and match info */}
            <div className="enhanced-button-header">
              <span className="fw-medium text-start" style={{ fontSize: '0.9rem' }}>
                {submissionType === 'email' && <FaEnvelope className="me-1" size="0.8em" />}
                {lender.name}
              </span>
              
              {/* Match info badge */}
              {hasCriteria && (
                <Badge 
                  bg={isAutoDecline ? 'danger' : (incomplete.length > 0 ? 'warning' : scoreVariant)}
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                >
                  {isAutoDecline ? 'Decline' : `${matched.length}/${totalCriteria}`}
                </Badge>
              )}
            </div>
            
            {/* Bottom row: Loading spinner or additional info */}
            {isSubmitting ? (
              <div className="enhanced-button-footer">
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2 spinner-animation"
                />
                <span style={{ fontSize: '0.8rem' }}>
                  {submissionType === 'email' ? 'Sending...' : 'Submitting...'}
                </span>
              </div>
            ) : hasCriteria && incomplete.length > 0 && !isAutoDecline ? (
              <span className="text-warning enhanced-button-footer">
                {incomplete.length} pending
              </span>
            ) : hasCriteria && score > 0 && !isAutoDecline ? (
              <span className="enhanced-button-footer">
                {score}% match
              </span>
            ) : null}
          </div>
        </Button>
        
        {/* Details button - only show if criteria exist */}
        {hasCriteria && (
          <div className="d-flex justify-content-end mt-1">
            <Button
              variant="link"
              size="sm"
              className="p-0 details-button"
              onClick={handleDetailsClick}
              disabled={isSubmitting}
            >
              Details
            </Button>
          </div>
        )}
      </div>

      {/* Match details popup */}
      <MatchDetailsPopup
        show={showMatchPopup}
        onClose={() => setShowMatchPopup(false)}
        lenderName={lender.name}
        score={score}
        matchedCriteria={matched}
        missedCriteria={missed}
        incompleteCriteria={incomplete}
        isAutoDecline={isAutoDecline}
      />
    </>
  );
};

export default EnhancedLenderButton; 